/**
 * The status and payment-method filters of a vendor's orders list, held in
 * the URL.
 *
 * A dashboard card opens the list with `?delivery=` or `?method=` set
 * (utilities/orderDeepLinks.js); the list reads them here, sends them to the
 * server with the page request (utils/orderListFilters.js applies them, so
 * they cut across pages), and writes them back when the operator changes a
 * pill. In the URL, a filtered view survives a reload and is a link that can
 * be sent to a colleague, and the browser's Back returns to the dashboard.
 *
 *   const { delivery, method, filters, filterKey, setDelivery, setMethod, clear } = useOrderListFilters();
 *
 *   delivery   the status list, upper-cased ([] is "any status")
 *   method     the payment-method list ([] is "any method")
 *   filters    { delivery, method } as comma strings, for the API call;
 *              empty keys left out
 *   filterKey  the filters as one string, for an effect's dependency list
 */
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

const listOf = (value) => String(value || "").split(",").map((s) => s.trim()).filter(Boolean);
const PARAM_KEYS = ["delivery", "method"];

export default function useOrderListFilters() {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const delivery = useMemo(() => listOf(params.get("delivery")).map((s) => s.toUpperCase()), [params]);
  const method = useMemo(() => listOf(params.get("method")).map((s) => s.toUpperCase()), [params]);

  const filters = useMemo(() => {
    const out = {};
    if (delivery.length) out.delivery = delivery.join(",");
    if (method.length) out.method = method.join(",");
    return out;
  }, [delivery, method]);
  const filterKey = PARAM_KEYS.map((k) => `${k}=${filters[k] || ""}`).join("&");

  const write = useCallback((key, value) => {
    const next = new URLSearchParams(location.search);
    const text = (Array.isArray(value) ? value : listOf(value)).map((v) => String(v).trim()).filter(Boolean).join(",");
    if (text) next.set(key, text);
    else next.delete(key);
    const qs = next.toString();
    history.push(qs ? `${location.pathname}?${qs}` : location.pathname);
  }, [history, location.pathname, location.search]);

  const setDelivery = useCallback((value) => write("delivery", value), [write]);
  const setMethod = useCallback((value) => write("method", value), [write]);
  const clear = useCallback(() => {
    const next = new URLSearchParams(location.search);
    PARAM_KEYS.forEach((k) => next.delete(k));
    const qs = next.toString();
    history.push(qs ? `${location.pathname}?${qs}` : location.pathname);
  }, [history, location.pathname, location.search]);

  return { delivery, method, filters, filterKey, setDelivery, setMethod, clear, active: delivery.length + method.length };
}
